import React, { forwardRef } from 'react';
import { Button, Icon, makeStyles, Paper, Typography, Grid, Theme, Checkbox, Tooltip } from '@material-ui/core';
import MaterialTable, { Action, Column, Icons, Options, Localization, Components, MTableToolbar } from 'material-table';

import { PageLocation } from '../enum/PageLocation';
import { theme } from '../../theme';
import { HomeContext } from '../../Features/Home/HomeContext';
import { TableCommonDateFilter } from './TableCommonDateFilter';
import { AuthContext } from '../../Features/Authentication/AuthContext';

export interface ITableCommonToolbar {
    title?: string,
    subtitle?: string,
    buttonText?: string,
    onButtonClick?: () => void
}

interface ITableCommonProps {
    tableData: any[],
    tableColumns: Column<any>[],
    emptyDataSourceMessage: string,
    tableActions?: (Action<any> | ((rowData: any) => Action<any>))[] | undefined,
    extraOptions?: Options<any> | undefined,
    toolbar?: ITableCommonToolbar,
    addNewButtonText?: string,
    addNewButtonPageLocation?: PageLocation,
    style?: React.CSSProperties,
    onRowClick?: (event: any, value: any) => void,
    paperElevation?: number,
    cssProperties?: ICssProperties,
    findParent?: boolean,
    editable?: any | undefined,
    extraLocalization?: Localization | undefined,
    onSelectionChange?: (rows: any[]) => void,
    enableValidationColumn?: boolean,
    validationActions?: (Action<any> | ((rowData: any) => Action<any>))[],
    rejectionActions?: (Action<any> | ((rowData: any) => Action<any>))[],
}

interface ICssProperties {
    tablePadding?: string,
    tableBorderRadius?: string,
}

const useStyles = makeStyles((theme: Theme) => ({
    '@global': {
        'tr > td': (props: ICssProperties) => ({
            padding: `${props.tablePadding || '8px'} !important`
        }),
    },
    toolbarText: {
        width: '100%',
        display: 'block',
        alignItems: 'center',
        textAlign: 'center',
    },
    toolbarTitle: {
        flex: '1 1 100%',
        margin: '24px 0px 4px'
    },
    toolbarSubtitle: {
        flex: '1 1 100%',
        margin: '4px 0px 36px',
    },
    paper: (props: ICssProperties) => ({
        borderRadius: props.tableBorderRadius || '8px',
        width: '100%',
        overflowX: 'auto'
    }),
    addNewButton: {
        justifyContent: 'left',
        paddingLeft: 0,
        margin: '12px 92px 24px',
        '&:hover': {
            textDecorationLine: 'underline',
        },
    },
    sortIcon: {
        fontSize: '14px',
        transform: 'rotate(90deg)',
        alignItems: 'center',
    },
    editIcon: {
        fontSize: '20px',
        order: -1,
    },
    exportIcon: {
        fontSize: '20px',
        color: theme.palette.primary.main
    }
}));

export function TableCommonWithValidate({
    tableData,
    tableColumns,
    emptyDataSourceMessage,
    tableActions,
    extraOptions,
    toolbar,
    addNewButtonText,
    addNewButtonPageLocation,
    style,
    onRowClick,
    paperElevation,
    cssProperties,
    findParent,
    editable,
    extraLocalization,
    onSelectionChange,
    validationActions,
    rejectionActions,
}: ITableCommonProps) {

    const classes = useStyles(cssProperties || {});

    const homeContext = React.useContext(HomeContext);
    const authContext = React.useContext(AuthContext);

    const getColumns = () => {
        const columnsTemp = [...tableColumns];
        //Alterando o componente de filtro quando é data, para fins de tradução
        columnsTemp.filter(c => c.type === 'date').forEach((column: Column<any>) => {
            column.filterComponent = (props: any) => TableCommonDateFilter(props);
        });

        const hasResponsavelReputacaoAccess = authContext.login.user.profile?.functionalities
            ?.some(f => f.description === 'Acesso Responsável Reputação');

        if (hasResponsavelReputacaoAccess) {
            columnsTemp.push({
                title: 'Autorizado',
                field: 'autorizado',
                render: rowData => {
                    const action = validationActions?.find(() => true);
                    if (!action) return null;

                    const finalAction = typeof action === 'function' ? action(rowData) : action;

                    return (
                        <Checkbox
                            checked={!!rowData.autorizado}
                            onChange={(e) => finalAction.onClick?.(e, rowData)}
                            disabled={finalAction.disabled}
                            color="primary"
                        />
                    );
                },
                sorting: false,
                filtering: false,
                cellStyle: { width: 48, textAlign: 'center' },
                headerStyle: { width: 48, textAlign: 'center' }
            });

            columnsTemp.push({
                title: 'Reprovado',
                field: 'reprovado',
                render: rowData => {
                    const action = rejectionActions?.find(() => true);
                    if (!action) return null;

                    const finalAction = typeof action === 'function' ? action(rowData) : action;

                    return (
                        rowData.reprovado ? (
                            <Tooltip
                                title={<span style={{ fontSize: '1.1rem', fontWeight: 500, color: '#fff', whiteSpace: 'pre-line' }}>{rowData.observacaoRejeicao || ''}</span>}
                                placement="top"
                                arrow
                            >
                                <span>
                                    <Checkbox
                                        checked={!!rowData.reprovado}
                                        onChange={(e) => finalAction.onClick?.(e, rowData)}
                                        disabled={finalAction.disabled}
                                        color="primary"
                                    />
                                </span>
                            </Tooltip>
                        ) : (
                            <Checkbox
                                checked={!!rowData.reprovado}
                                onChange={(e) => finalAction.onClick?.(e, rowData)}
                                disabled={finalAction.disabled}
                                color="primary"
                            />
                        )
                    );
                },
                sorting: false,
                filtering: false,
                cellStyle: { width: 48, textAlign: 'center' },
                headerStyle: { width: 48, textAlign: 'center' }
            });
        }


        return columnsTemp;
    };

    const baseOptions: Options<any> = {
        search: false,
        draggable: false,
        thirdSortClick: false,
        emptyRowsWhenPaging: false,
        sorting: true,
        headerStyle: {
            fontSize: '16px',
            whiteSpace: 'nowrap',
            padding: theme.spacing(1)
        },
        rowStyle: {
            fontSize: '16px',
        },
        padding: 'dense',
    };

    const baseLocalization: Localization = {
        pagination: {
            labelRowsSelect: '',
            labelDisplayedRows: '{from}-{to} de {count}',
            firstTooltip: 'Primeira página',
            previousTooltip: 'Página anterior',
            nextTooltip: 'Próxima página',
            lastTooltip: 'Última página',
        },
        body: {
            emptyDataSourceMessage: emptyDataSourceMessage,
            filterRow: {
                filterTooltip: 'Filtrar'
            },
        },
        header: {
            actions: 'Ações'
        },
        toolbar: {
            exportTitle: 'Exportar',
            exportPDFName: 'Exportar como PDF',
            exportCSVName: 'Exportar como CSV'
        }
    };

    const tableLocalization: Localization = { ...baseLocalization, ...extraLocalization };

    let tableOptions: Options<any> = { ...baseOptions, ...extraOptions };

    if (tableActions)
        tableOptions = { ...tableOptions, actionsColumnIndex: 6 };

    let tableComponent: Components | undefined = {};

    if (toolbar && toolbar.title && toolbar.subtitle) {
        tableComponent = {
            Toolbar: function customToolbarStyle() {
                return (
                    <div className={classes.toolbarText}>
                        <Typography
                            className={classes.toolbarTitle}
                            variant="h6"
                            component="div">
                            {toolbar.title}
                        </Typography>
                        <Typography
                            className={classes.toolbarSubtitle}
                            variant="body1"
                            component="div">
                            {toolbar.subtitle}
                        </Typography>
                    </div>
                );
            },
        };
    }
    else if (toolbar && toolbar.buttonText && toolbar.onButtonClick) {
        tableComponent = {
            Toolbar: function customToolbarStyle(props) {
                return (
                    <Grid
                        container
                        direction="row"
                        justify="space-between"
                        alignItems="center">
                        <Grid item>
                            <Button
                                startIcon={<Icon color="primary">filter_list</Icon>}
                                size="large"
                                onClick={toolbar.onButtonClick}>
                                {toolbar.buttonText}
                            </Button>
                        </Grid>
                        <Grid item>
                            <MTableToolbar {...props} title={false} />
                        </Grid>
                    </Grid>
                );
            },
        };
    }

    const changeIcon = (props: any, ref: any) => (
        <div {...props} ref={ref}>
            <Icon className={classes.sortIcon}>compare_arrows</Icon>
        </div>
    );

    const editIcon = () => (
        <Icon className={classes.editIcon}>edit</Icon>
    );

    const exportIcon = () => (
        <Icon className={classes.exportIcon}>get_app</Icon>
    );

    const tableIcons: Icons = {
        SortArrow: forwardRef(changeIcon),
        Edit: forwardRef(editIcon),
        Export: forwardRef(exportIcon)
    };

    const baseStyles: React.CSSProperties = {
        boxShadow: 'none',
    };

    const getStyle = (): React.CSSProperties => {
        if (style)
            return { ...style, ...baseStyles };
        else
            return { ...baseStyles, padding: theme.spacing(1, 3) };
    };

    const getAddNewButton = () => {
        return (
            <Grid item>
                <Button
                    onClick={() => { addNewButtonPageLocation && homeContext.setPageLocation(addNewButtonPageLocation); }}
                    className={classes.addNewButton}
                    startIcon={<Icon color='primary'>add</Icon>}>
                    {addNewButtonText}
                </Button>
            </Grid>
        );
    };

    const getElevation = () => {
        if (paperElevation == undefined)
            return 3;
        else
            return paperElevation;
    };

    return (
        <Grid container>
            <Paper
                elevation={getElevation()}
                className={classes.paper}>
                <MaterialTable
                    data={tableData}
                    onRowClick={onRowClick}
                    icons={tableIcons}
                    columns={getColumns()}
                    options={tableOptions}
                    actions={tableActions}
                    localization={tableLocalization}
                    components={tableComponent}
                    parentChildData={(row, rows) => findParent && (rows.find(a => a.id === row.parentId))}
                    editable={editable}
                    style={getStyle()}
                    onSelectionChange={onSelectionChange} />

                {addNewButtonText && getAddNewButton()}
            </Paper>
        </Grid>
    );
}